import { Decision, Draft, Escalation, NiwtAgent } from './types';

export const mockAgents: NiwtAgent[] = [
  {
    id: 'niwtco-001',
    display_name: 'Niwtco',
    type: 'niwtco',
    role: 'Chief of Staff AI',
    coverage: 94,
    freshness_minutes: 12,
  },
  {
    id: 'emp-sarah-001',
    display_name: 'Sarah Chen',
    type: 'employee',
    role: 'VP of Sales',
    coverage: 87,
    freshness_minutes: 45,
  },
  {
    id: 'emp-marcus-002',
    display_name: 'Marcus Rivera',
    type: 'employee',
    role: 'Head of Engineering',
    coverage: 91,
    freshness_minutes: 30,
  },
  {
    id: 'emp-priya-003',
    display_name: 'Priya Nair',
    type: 'employee',
    role: 'Finance Director',
    coverage: 78,
    freshness_minutes: 120,
  },
];

export const mockDecisions: Decision[] = [
  {
    id: 'dec-001',
    title: 'Approve Q2 Pricing Tier Increase (+12%)',
    summary:
      'Enterprise pricing has remained flat for 18 months. Three competitors raised prices in Q1. Your margins on the Pro tier are down 4 pts YoY. Niwtco recommends a 12% increase effective May 1.',
    why_now: 'Renewal window opens April 15 — changes must be communicated 30 days prior.',
    impact: { label: 'Annual Revenue Impact', value: '+$184k ARR' },
    time_estimate_minutes: 10,
    recommended_delegate_agent_id: 'emp-priya-003',
    confidence: 0.82,
    receipts: [
      {
        source_type: 'email',
        source_id: 'email-p-001',
        title: 'Priya: Q1 Margin Analysis — Pro Tier',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        source_type: 'doc',
        source_id: 'doc-comp-001',
        title: 'Competitor Pricing Matrix (March 2026)',
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        source_type: 'email',
        source_id: 'email-s-002',
        title: 'Sarah: Enterprise renewal pipeline — April',
        timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
  },
  {
    id: 'dec-002',
    title: 'Approve Senior Backend Engineer Hire (Offer #ENG-2026-07)',
    summary:
      'Marcus has extended a verbal offer to a senior backend candidate. Comp is $162k base + equity. The role was approved in January headcount plan.',
    why_now: 'Candidate has a competing offer expiring Tuesday.',
    impact: { label: 'Headcount Cost', value: '$162k/yr + equity' },
    time_estimate_minutes: 15,
    recommended_delegate_agent_id: 'emp-marcus-002',
    confidence: 0.55,
    receipts: [
      {
        source_type: 'email',
        source_id: 'email-m-003',
        title: 'Marcus: Offer letter — ENG-2026-07',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      },
    ],
  },
  {
    id: 'dec-003',
    title: 'Renew Datadog Contract (Annual, $34k)',
    summary:
      "Current Datadog contract expires March 31. Marcus's team has evaluated usage — they use ~70% of current limits. Renewing at the same tier is recommended over downgrading due to oncall reliability.",
    why_now: 'Vendor requires 14-day cancellation notice; deadline is March 17.',
    impact: { label: 'Annual Spend', value: '$34k (same tier)' },
    time_estimate_minutes: 5,
    recommended_delegate_agent_id: 'emp-marcus-002',
    confidence: 0.91,
    receipts: [
      {
        source_type: 'email',
        source_id: 'email-dd-001',
        title: 'Datadog: Renewal notice — expires March 31',
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        source_type: 'doc',
        source_id: 'doc-dd-002',
        title: 'Datadog usage report — Feb 2026',
        timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        source_type: 'email',
        source_id: 'email-m-004',
        title: 'Marcus: Infra cost review — Q1',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        source_type: 'ticket',
        source_id: 'ticket-dd-003',
        title: 'Oncall: Datadog alert saved 3hr incident — Feb 12',
        timestamp: new Date(Date.now() - 17 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
  },
  {
    id: 'dec-004',
    title: 'Respond to Acme Corp Escalation (Contract Risk)',
    summary:
      "Sarah flagged that Acme Corp's VP of Ops sent a formal escalation email. They are unhappy with onboarding delays and are considering invoking the 30-day exit clause. This is your top 3 account ($290k ARR).",
    why_now: 'Sarah needs your executive response by EOD to defuse before it reaches their CEO.',
    impact: { label: 'At-Risk ARR', value: '$290k' },
    time_estimate_minutes: 20,
    recommended_delegate_agent_id: 'emp-sarah-001',
    confidence: 0.74,
    receipts: [
      {
        source_type: 'email',
        source_id: 'email-acme-001',
        title: 'Acme Corp: Formal escalation — onboarding delays',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      },
      {
        source_type: 'crm',
        source_id: 'crm-acme-002',
        title: 'CRM: Acme Corp account notes — Feb/Mar',
        timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
  },
  {
    id: 'dec-005',
    title: 'Reallocate $28k from Trade Shows to Performance Marketing',
    summary:
      'Q1 trade show ROI came in at 0.4x (below 1.0x threshold). Sarah proposes shifting $28k of the remaining H1 events budget to paid search and LinkedIn campaigns where you are currently getting 2.1x ROAS.',
    why_now: 'April campaign planning closes this week — late changes require Director approval.',
    impact: { label: 'Budget Shift', value: '$28k reallocation' },
    time_estimate_minutes: 20,
    recommended_delegate_agent_id: 'emp-sarah-001',
    confidence: 0.48,
    receipts: [],
  },
];

export const mockDrafts: Draft[] = [
  {
    id: 'draft-001',
    title: 'Board Update: Q1 Performance & Q2 Outlook',
    type: 'email',
    prepared_for: 'Board of Directors',
    prepared_by_agent_id: 'niwtco-001',
    created_at: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
  },
  {
    id: 'draft-002',
    title: 'Acme Corp — Executive Response to Escalation',
    type: 'email',
    prepared_for: 'Acme Corp VP of Ops',
    prepared_by_agent_id: 'emp-sarah-001',
    created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
  {
    id: 'draft-003',
    title: 'Performance Review: Marcus Rivera — Q1 2026',
    type: 'memo',
    prepared_for: 'HR File',
    prepared_by_agent_id: 'niwtco-001',
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
];

export const mockEscalations: Escalation[] = [
  {
    id: 'esc-001',
    title: 'Acme Corp at risk of churn (exit clause invoked)',
    severity: 'high',
    source_agent_id: 'emp-sarah-001',
    created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    summary:
      "Acme Corp VP has invoked the 30-day exit clause. Sarah needs executive-level response by EOD. $290k ARR at risk.",
  },
  {
    id: 'esc-002',
    title: 'SOC 2 evidence submission deadline: March 10',
    severity: 'medium',
    source_agent_id: 'niwtco-001',
    created_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    summary:
      'Auditor requires access log exports and vendor security questionnaires by March 10. Three items still outstanding.',
  },
];
